<template>
  <div :class="$style.form">
    <div :key="formKey" :class="$style.body">
      <template v-if="currentStep <= totalSteps">
        <HeaderSection
          :current-step="currentStep"
          :total-steps="totalSteps"
          @close="handleClose"
        />

        <SectionLoader
          :active="isSectionLoading || isActionPending"
          :class="$style.content"
        >
          <PersonalDataSection
            v-if="currentStep === 1"
            @client-selected="handleClientSelected"
            @dirty="markStepOneDirty"
            @persisted="markStepOnePersisted"
          />
          <VehicleParametersSection
            v-else-if="currentStep === 2"
            :key="`vehicle-${selectedClient?.id || 'new'}`"
            :initial-client="selectedClient"
          />
          <ServicesSection v-else-if="currentStep === 3" />
          <ScheduleSection v-else-if="currentStep === 4 && !isQueueMode" />
        </SectionLoader>

        <FormFooter
          :current-step="currentStep"
          :can-proceed="canProceed"
          :consent-accepted="isConsentAccepted"
          :disabled="isActionPending"
          :show-close="currentStep === 1"
          :total-steps="totalSteps"
          @update:consent-accepted="isConsentAccepted = $event"
          @back="goBack"
          @clear="clearOrder"
          @next="goNext"
          @submit="confirmOrder"
          @close="handleClose"
        />
      </template>

      <SuccessScreen
        v-else
        :order="createdOrder"
        :is-queue-mode="isQueueMode"
        @close="closeSuccessScreen"
      />
    </div>
  </div>
</template>

<script>
import FormFooter from "./FormFooter.vue";
import HeaderSection from "./sections/Header.vue";
import PersonalDataSection from "./sections/PersonalData.vue";
import ScheduleSection from "./sections/Schedule.vue";
import ServicesSection from "./sections/Services.vue";
import SuccessScreen from "./SuccessScreen.vue";
import VehicleParametersSection from "./sections/VehicleParameters.vue";
import SectionLoader from "@/components/common/SectionLoader.vue";
import asyncActionMixin from "@/mixins/asyncActionMixin";
import { mapState } from "vuex";

const DEFAULT_TOTAL_STEPS = 4;
const QUEUE_TOTAL_STEPS = 3;

export default {
  name: "RequestsForm",
  mixins: [asyncActionMixin],
  components: {
    FormFooter,
    HeaderSection,
    PersonalDataSection,
    ScheduleSection,
    ServicesSection,
    SuccessScreen,
    VehicleParametersSection,
    SectionLoader,
  },
  data() {
    return {
      currentStep: 1,
      formKey: 0,
      selectedClient: null,
      isConsentAccepted: false,
      createdOrder: null,
      isStepOneDirty: false,
    };
  },
  computed: {
    ...mapState({
      order: (state) => state.orders.order,
      categories: (state) => state.complexes.complexes,
    }),
    isQueueMode() {
      const type = Array.isArray(this.$route?.query?.type)
        ? this.$route.query.type[0]
        : this.$route?.query?.type;
      return String(type || "").toLowerCase() === "queue";
    },
    totalSteps() {
      return this.isQueueMode ? QUEUE_TOTAL_STEPS : DEFAULT_TOTAL_STEPS;
    },
    successStep() {
      return this.totalSteps + 1;
    },
    canProceed() {
      if (this.currentStep === 1) {
        const phoneDigits = String(
          this.order?.client_data?.phone_number || ""
        ).replace(/\D/g, "");
        return phoneDigits.length >= 11;
      }
      if (this.currentStep === 2) {
        return Boolean(
          this.order?.car?.make &&
            this.order?.car?.model &&
            this.order?.car?.generation &&
            (this.order?.car?.tires?.summer_tires?.first_tires ||
              this.order?.car?.tires?.first_tires)
        );
      }
      if (this.currentStep === 3) {
        if (this.isQueueMode) {
          return Boolean(this.order?.complex && this.isConsentAccepted);
        }
        return Boolean(this.order?.complex);
      }
      if (this.currentStep === 4) {
        return Boolean(this.isConsentAccepted);
      }
      return false;
    },
  },
  watch: {
    isQueueMode: {
      immediate: true,
      handler() {
        this.applyQueueOrderDefaults();
      },
    },
  },
  methods: {
    applyQueueOrderDefaults() {
      this.$store.commit("orders/setOrderField", {
        field: "fast_source",
        value: this.isQueueMode ? "crm" : "online",
      });
    },
    async resetFormState() {
      this.selectedClient = null;
      this.isConsentAccepted = false;
      this.isStepOneDirty = false;
      this.$store.commit("orders/resetOrder");
      this.$store.commit("complexes/resetComplexes");
      this.applyQueueOrderDefaults();
      this.formKey += 1;
      await this.$store.dispatch("department/fetchWorkDays");
    },
    handleClientSelected(client) {
      this.selectedClient = client;
    },
    markStepOneDirty() {
      this.isStepOneDirty = true;
    },
    markStepOnePersisted() {
      this.isStepOneDirty = false;
    },
    async goNext() {
      if (!this.canProceed) return;
      await this.withAction(async () => {
        let shouldFetchComplexes = false;
        let complexesPayload = null;

        if (this.currentStep === 1) {
          if (!this.order?.id || this.isStepOneDirty) {
            const { error } = await this.$store.dispatch("orders/saveOrder");
            if (error) return;
            this.isStepOneDirty = false;
          }
        }

        if (this.currentStep === 2) {
          const tireId =
            this.order?.car?.tires?.summer_tires?.first_tires ||
            this.order?.car?.tires?.first_tires;
          const secondTireId =
            this.order?.car?.tires?.summer_tires?.second_tires ||
            this.order?.car?.tires?.second_tires ||
            null;
          shouldFetchComplexes = Boolean(tireId && !this.categories.length);
          complexesPayload = shouldFetchComplexes
            ? {
                tireId,
                secondTireId,
              }
            : null;
        }

        if (this.currentStep < this.totalSteps) {
          this.currentStep += 1;
        }

        if (this.currentStep === 3 && shouldFetchComplexes && complexesPayload) {
          await this.$store.dispatch(
            "complexes/fetchTiresComplexes",
            complexesPayload
          );
        }
      });
    },
    goBack() {
      if (this.currentStep > 1) {
        this.currentStep -= 1;
      }
    },
    async confirmOrder() {
      if (!this.canProceed) return;

      await this.withAction(async () => {
        if (!this.order?.client_data?.phone_number?.trim()) {
          this.$store.commit("orders/setFieldError", {
            field: "client_data.phone_number",
            value: ["Обязательное поле."],
          });
          this.currentStep = 1;
          return;
        }

        let orderId = this.order?.id;
        if (!orderId) {
          const { value, error } = await this.$store.dispatch(
            "orders/createOrder"
          );
          if (error || !value?.id) return;
          orderId = value.id;
        }

        const { value, error } = await this.$store.dispatch(
          "orders/confirmOrder",
          {
            id: orderId,
          }
        );
        if (error || !value) return;

        this.createdOrder = value;
        this.currentStep = this.successStep;
      });
    },
    async closeSuccessScreen() {
      this.createdOrder = null;
      this.currentStep = 1;
      await this.resetFormState();
    },
    async clearOrder() {
      await this.withAction(async () => {
        this.currentStep = 1;
        await this.resetFormState();
      });
    },
    handleClose() {
      if (this.currentStep === this.successStep) {
        this.closeSuccessScreen();
        return;
      }
      if (this.currentStep === 1 && window.history.length > 1) {
        window.history.back();
        return;
      }
      if (this.currentStep > 1) {
        this.goBack();
      }
    },
  },
};
</script>

<style lang="scss" module>
.form {
  @include container;
  background-color: $bg-white;
  border-radius: 1.5rem;
  height: calc(100vh - 3rem);
  overflow: hidden;
  position: relative;
}

.body {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.content {
  flex: 1 1 auto;
  overflow: auto;
  @include scrollbarY;
}
</style>
